import React, { useEffect, useState } from "react";
import { fetchIESummary } from "../../api/ie.api";

const fmtINR = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function IEDashboard({
  societyId,
  fyId,
  financialYear,
  onGoToReport,
  onGoToJournal,
}) {
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");

  useEffect(() => {
    if (!societyId || !fyId || !financialYear) return;
    loadSummary();
  }, [societyId, fyId, financialYear]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetchIESummary(societyId, fyId, financialYear);
      setSummary(res.data);
    } catch (err) {
      setError("Failed to load summary. Please try again.");
      console.error("IE Dashboard error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="ie-loading">
        <div className="ie-spinner"></div>
        Loading summary...
      </div>
    );
  }

  if (error) {
    return (
      <div className="ie-alert error">
        <span>⚠</span>
        <span>{error}</span>
      </div>
    );
  }

  const totalIncome      = summary?.totalIncome      || 0;
  const totalExpenditure = summary?.totalExpenditure || 0;
  const surplusDeficit   = summary?.surplusDeficit   || 0;
  const isSurplus        = summary?.isSurplus        ?? true;
  const unclassified     = summary?.unclassified     || [];

  const incomeHeads      = Object.entries(summary?.income      || {});
  const expenditureHeads = Object.entries(summary?.expenditure || {});

  return (
    <div>

      {/* ── Stat cards ─────────────────────────────────────────────────── */}
      <div className="ie-stats-row">

        <div className="ie-stat-card income">
          <div className="ie-stat-label">Total Income</div>
          <div className="ie-stat-value income-val">₹{fmtINR(totalIncome)}</div>
          <div className="ie-stat-note">
            {incomeHeads.length} head{incomeHeads.length !== 1 ? "s" : ""}
          </div>
        </div>

        <div className="ie-stat-card expense">
          <div className="ie-stat-label">Total Expenditure</div>
          <div className="ie-stat-value expense-val">₹{fmtINR(totalExpenditure)}</div>
          <div className="ie-stat-note">
            {expenditureHeads.length} head{expenditureHeads.length !== 1 ? "s" : ""}
          </div>
        </div>

        <div className="ie-stat-card surplus">
          <div className="ie-stat-label">
            {isSurplus ? "Surplus" : "Deficit"} for the Year
          </div>
          <div className={`ie-stat-value ${isSurplus ? "surplus-val" : "deficit-val"}`}>
            ₹{fmtINR(Math.abs(surplusDeficit))}
          </div>
          <div className="ie-stat-note">
            {isSurplus ? "Carried to General Fund" : "Charged to General Fund"}
          </div>
        </div>

        <div className="ie-stat-card journal">
          <div className="ie-stat-label">Journal Adjustments</div>
          <div className="ie-stat-value journal-val">
            {unclassified.length > 0 ? unclassified.length : "0"}
          </div>
          <div className="ie-stat-note">
            {unclassified.length > 0
              ? "Heads need classification"
              : "Manual year-end entries"}
          </div>
        </div>

      </div>

      {/* ── Unclassified heads warning ──────────────────────────────────── */}
      {unclassified.length > 0 && (
        <div className="ie-alert warning" style={{ marginBottom: 20 }}>
          <span>⚠</span>
          <div>
            <strong>{unclassified.length} ledger head{unclassified.length !== 1 ? "s" : ""} could not be auto-classified.</strong>
            {" "}These will not appear in the I&E report until classified.
            Go to the Ledger Classification tab to fix them.
          </div>
        </div>
      )}

      {/* ── Income breakdown ─────────────────────────────────────────────── */}
      <div className="ie-card">
        <div className="ie-card-header">
          <div className="ie-card-title">Income Heads</div>
          <span style={{ fontSize: 12, color: "#9a8f85" }}>Accrual basis</span>
        </div>
        <div className="ie-card-body" style={{ padding: 0 }}>
          {incomeHeads.length === 0 ? (
            <div className="ie-empty">
              <div className="ie-empty-text">No income entries found</div>
              <div className="ie-empty-sub">
                Check YEA Income entries and Maintenance Billing for {financialYear}
              </div>
            </div>
          ) : (
            <table className="je-table" style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th>Ledger Head</th>
                  <th style={{ textAlign: "right" }}>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {incomeHeads.map(([head, amount]) => (
                  <tr key={head}>
                    <td>{head}</td>
                    <td style={{
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      color: "#b7791f",
                      fontWeight: 500,
                    }}>
                      {fmtINR(amount)}
                    </td>
                  </tr>
                ))}
                <tr style={{ background: "#faf8f5", fontWeight: 600 }}>
                  <td>Total Income</td>
                  <td style={{
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                    color: "#b7791f",
                    borderTop: "1px solid #2a221c",
                  }}>
                    {fmtINR(totalIncome)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Expenditure breakdown ─────────────────────────────────────────── */}
      <div className="ie-card">
        <div className="ie-card-header">
          <div className="ie-card-title">Expenditure Heads</div>
          <span style={{ fontSize: 12, color: "#9a8f85" }}>From YEA entries</span>
        </div>
        <div className="ie-card-body" style={{ padding: 0 }}>
          {expenditureHeads.length === 0 ? (
            <div className="ie-empty">
              <div className="ie-empty-text">No expenditure entries found</div>
              <div className="ie-empty-sub">
                Add expense entries in YEA module for {financialYear}
              </div>
            </div>
          ) : (
            <table className="je-table" style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th>Ledger Head</th>
                  <th style={{ textAlign: "right" }}>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {expenditureHeads.map(([head, amount]) => (
                  <tr key={head}>
                    <td>{head}</td>
                    <td style={{
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      color: "#c0392b",
                      fontWeight: 500,
                    }}>
                      {fmtINR(amount)}
                    </td>
                  </tr>
                ))}
                <tr style={{ background: "#faf8f5", fontWeight: 600 }}>
                  <td>Total Expenditure</td>
                  <td style={{
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                    color: "#c0392b",
                    borderTop: "1px solid #2a221c",
                  }}>
                    {fmtINR(totalExpenditure)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Action buttons ───────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
        <button className="ie-btn purple" onClick={onGoToJournal}>
          + Add Journal Entry
        </button>
        <button className="ie-btn primary" onClick={onGoToReport}>
          Generate I&E Report →
        </button>
        <button className="ie-btn" onClick={loadSummary}>
          ↻ Refresh
        </button>
      </div>

    </div>
  );
}
